<template>
  <header class="top-bar p-3 flex align-items-center justify-content-between sticky top-0 z-10" :class="userTypeClass">
    <div class="flex align-self-start">
      <Button class="p-button-text p-button-rounded menu-button p-button-lg mr-3 lg:hidden"
        @click="$emit('toggle-sidebar')" icon="pi pi-bars" />
    </div>

    <div class="flex flex-wrap align-self-start justify-content-end gap-2 pt-2 nav-buttons">
      <Button class="p-button-text btn-topbar p-button-sm blink-1" label="SOS - Call 119" @click="handleSosClick"
        v-if="role === 'student'" />

      <!-- Desktop-only buttons -->
      <!-- <Button class="p-button-text btn-topbar p-button-sm desktop-only" label="Learning Library" /> -->
      <Button class="p-button-text btn-topbar p-button-sm desktop-only" label="Find a Tutor" @click="findTutor" v-if="role === 'student'"/>
      <Button class="p-button-text btn-topbar p-button-sm desktop-only" label="Exam Preparation" @click="isDialogVisible = true"/>
      <Button class="p-button-text btn-topbar p-button-sm desktop-only" label="About Us" @click="aboutUs" />

      <!-- Always visible icons -->
      <Button class="p-button-text btn-topbar p-button-sm find-tutor-mobile" icon="pi pi-search" @click="findTutor" v-if="role === 'student'" title="Find a Tutor" />
      <Button class="p-button-text btn-topbar p-button-sm" icon="pi pi-home" @click="home" />
      <Button class="p-button-text btn-topbar p-button-sm relative" icon="pi pi-comments" @click="toggleChatDropdown">
        <span class="chat-badge" v-if="unreadCount > 0">
          {{ unreadCount > 99 ? '99+' : unreadCount }}
        </span>
      </Button>
      <Button class="p-button-text btn-topbar p-button-sm" icon="pi pi-cog" @click="settings" />

      <!-- Logout - Text on desktop, icon on mobile -->
      <Button class="p-button-text btn-topbar underlined-button p-button-sm hidden md:inline-flex" label="Log out"
        @click="logout" />
      <Button class="p-button-text btn-topbar p-button-sm md:hidden" icon="pi pi-sign-out" @click="logout" />
      <GoogleTranslate class="px-2 pt-2 google-translate" />

      <div class="flex justify-center md:justify-start">
        <img :src="image" alt="Profile Image" class="profile-image" />
      </div>
      <GoogleTranslate class="px-2 pt-2 md:hidden lg:hidden xl:hidden translator" />
    </div>
    <ComingSoonDialog v-model="isDialogVisible" :duration="3000" />
  </header>
</template>

<script setup>
import Button from 'primevue/button';
import { defineEmits, computed, defineProps, ref, onUnmounted, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore, chatClient } from "@/stores/authStore";
import GoogleTranslate from '@/components/GoogleTranslate.vue';
import ComingSoonDialog from '../ComingSoonDialog.vue';

const emit = defineEmits(['toggle-sidebar','open-chat']);
const props = defineProps({
  role: String,
  image: String
});
const authStore = useAuthStore();
const router = useRouter();

const unreadCount = ref(0);
const channels = ref([]);

const isDialogVisible = ref(false);

const emergencyCallLink = 'tel:119';

const userTypeClass = computed(() => {
  switch (props.role) {
    case 'student': return 'topbar-student';
    case 'tutor': return 'topbar-tutor';
    case 'institute': return 'topbar-institution';
    default: return 'topbar-default';
  }
});

const settings = () => {
  switch (props.role) {
    case 'student': return router.push('/studentSettings');
    case 'tutor': return router.push('/tutorSettings');
    case 'institute': return router.push('/instituteSettings');
    default: return router.push('/login');
  }
};
const home = () => {
  router.push('/');
}
const aboutUs = () => {
  router.push('/aboutUs');
}

const findTutor = () => {
  router.push('/matchTutor');
}
const handleSosClick = () => {
  window.location.href = emergencyCallLink;
};
const logout = () => {
  authStore.logout();
};

const updateUnreadCount = () => {
  try{
  if (chatClient.user && chatClient.state) {
    // If state.channels is empty, try to get channels from state memberships
    const channels = chatClient.state.channels || {};

    const channelsArray = Object.values(channels);

    if (channelsArray.length === 0) {
      console.log("No channels found in state yet.");
      unreadCount.value = 0;
      return;
    }

    unreadCount.value = channelsArray.reduce((sum, channel) => {
      console.log(`Channel ${channel.cid} has ${channel.countUnread()} unread messages.`);
      return sum + channel.countUnread();
    }, 0);
  } else {
    unreadCount.value = 0;
  }
} catch (error) {
  console.log(error);
}
};

onMounted(async () => {
  try{
  await authStore.connectToChat();
  channels.value = await chatClient.queryChannels(
    { type: 'messaging', members: { $in: [chatClient.userID] } },
    { last_message_at: -1 },
    { state: true, watch: true }
  );

  updateUnreadCount();
  chatClient.on('message.new', updateUnreadCount);
  chatClient.on('notification.mark_read', updateUnreadCount);
  chatClient.on('connection.recovered', updateUnreadCount);
  } catch (error) {
    console.log(error);
  }
});

onUnmounted(() => {
  try{
  chatClient.off('message.new', updateUnreadCount);
  chatClient.off('notification.mark_read', updateUnreadCount);
  } catch (error) {
    console.log(error);
  }
});

const toggleChatDropdown = async () => {
  emit('open-chat');
};

</script>

<style scoped>
.chat-badge {
  position: absolute;
  top: 5px;
  right: 5px;
  background-color: var(--orange-500);
  color: white;
  border-radius: 50%;
  padding: 2px 6px;
  font-size: 10px;
  font-weight: bold;
  min-width: 18px;
  text-align: center;
}

.top-bar {
  backdrop-filter: blur(10px);
  border-bottom: 1px solid var(--surface-border);
  height: 40vh;
  transition: background 0.5s ease-in-out;
  padding: 1rem;
}

.topbar-default {
  background: linear-gradient(360deg, #77D8D8 -35.88%, #005F5F 100%);
}

.topbar-student {
  background: linear-gradient(360deg, #FFB8C5 -35.88%, #A72E44 100%);
}

.topbar-tutor {
  background: linear-gradient(360deg, #77D8D8 -35.88%, #005F5F 100%);
}

.topbar-institution {
  background: var(--yellow-500);
}

button {
  transition: all 0.2s ease-in-out;
}

.btn-topbar {
  color: white;
  font-size: 14px;
  font-weight: 400;
}

.btn-topbar:hover {
  background-color: transparent !important;
  color: white !important;
  transform: scale(1.05);
}

.topbar-institution .btn-topbar {
  color: #1d1d1d;
}

.topbar-institution .btn-topbar:hover {
  color: #1d1d1d !important;
}

.menu-button {
  background-color: transparent;
  color: white;
  border: none;
}

.menu-button:hover {
  background-color: transparent !important;
  border: none !important;
  color: white !important;
  transform: scale(1.05);
}

.underlined-button {
  text-decoration: underline;
}

.profile-image {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 20px;
}

.find-tutor-mobile {
  display: none !important;
}

@media (max-width: 600px) {

  .nav-buttons .desktop-only {
    display: none !important;
  }

  .nav-buttons .find-tutor-mobile {
    display: inline-flex !important;
  }
}
.blink-1 {
  -webkit-animation: blink-1 3s infinite both;
  animation: blink-1 3s infinite both;

}

@media (max-width: 600px) {
  .blink-1 {
    font-size: 12px;

  }

  .google-translate {
    display: none !important;
  }

  .profile-image {
    width: 32px;
    height: 32px;
  }

}

@-webkit-keyframes blink-1 {

  0%,
  50%,
  100% {
    opacity: 1;
  }

  25%,
  75% {
    opacity: 0;
  }
}

@keyframes blink-1 {

  0%,
  50%,
  100% {
    opacity: 1;
  }

  25%,
  75% {
    opacity: 0;
  }
}
</style>
